import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { Injectable, PLATFORM_ID, inject } from '@angular/core';
import { EMPTY, Observable, of, share } from 'rxjs';

type WindowEventName = keyof WindowEventMap;
type DocumentEventName = keyof DocumentEventMap;
type EventTargetType = 'window' | 'document';

interface GlobalListenerOptions {
  capture: boolean;
  passive?: boolean;
}

export interface WindowGlobalEventOptions {
  capture?: boolean;
  passive?: boolean;
}

export interface DocumentGlobalEventOptions {
  capture?: boolean;
  passive?: boolean;
}

/**
 * Provides shared DOM event streams for `window` and `document`.
 *
 * Streams are cached by `{target,eventName,capture,passive}` so multiple subscribers
 * share a single native event listener. Cache entries are removed automatically when
 * the last subscriber unsubscribes.
 *
 * In non-browser environments (SSR), methods return `EMPTY`.
 */
@Injectable({
  providedIn: 'root',
})
export class GlobalEventsService {
  private readonly platformId = inject(PLATFORM_ID);
  private readonly document = inject(DOCUMENT);
  private readonly isBrowser = isPlatformBrowser(this.platformId);
  private readonly streams = new Map<string, Observable<Event>>();

  /**
   * Subscribes to a `window` event with optional listener flags.
   */
  onWindow<K extends WindowEventName>(
    eventName: K,
    options: WindowGlobalEventOptions = {},
  ): Observable<WindowEventMap[K]> {
    return this.getOrCreateStream<WindowEventMap[K]>('window', eventName, options);
  }

  /**
   * Subscribes to a `document` event with optional listener flags.
   * Useful for non-bubbling events observed via capture phase (e.g. `scroll`).
   */
  onDocument<K extends DocumentEventName>(
    eventName: K,
    options: DocumentGlobalEventOptions = {},
  ): Observable<DocumentEventMap[K]> {
    return this.getOrCreateStream<DocumentEventMap[K]>('document', eventName, options);
  }

  private getOrCreateStream<TEvent extends Event>(
    target: EventTargetType,
    eventName: WindowEventName | DocumentEventName,
    options: { capture?: boolean; passive?: boolean },
  ): Observable<TEvent> {
    const windowRef = this.getBrowserWindow();
    if (!windowRef) {
      return EMPTY;
    }

    const listenerOptions = this.normalizeListenerOptions(options);
    const cacheKey = this.buildCacheKey(target, eventName, listenerOptions);
    const cachedStream = this.streams.get(cacheKey);
    if (cachedStream) {
      return cachedStream as Observable<TEvent>;
    }

    const eventTarget: Window | Document = target === 'document' ? this.document : windowRef;
    const stream = new Observable<TEvent>((subscriber) => {
      const listener: EventListener = (event) => subscriber.next(event as TEvent);
      eventTarget.addEventListener(eventName, listener, {
        capture: listenerOptions.capture,
        passive: listenerOptions.passive,
      });

      return () =>
        eventTarget.removeEventListener(eventName, listener, {
          capture: listenerOptions.capture,
        });
    }).pipe(
      share({
        resetOnError: true,
        resetOnComplete: true,
        // Keep cache aligned with RxJS ref counting: remove stream when no subscribers remain.
        resetOnRefCountZero: () => {
          this.streams.delete(cacheKey);
          return of(null);
        },
      }),
    );

    this.streams.set(cacheKey, stream as Observable<Event>);

    return stream;
  }

  private getBrowserWindow(): Window | null {
    return this.isBrowser ? this.document.defaultView : null;
  }

  private normalizeListenerOptions(options: {
    capture?: boolean;
    passive?: boolean;
  }): GlobalListenerOptions {
    return {
      capture: options.capture ?? false,
      passive: options.passive,
    };
  }

  private buildCacheKey(
    target: EventTargetType,
    eventName: WindowEventName | DocumentEventName,
    options: GlobalListenerOptions,
  ): string {
    const passiveValue =
      options.passive === undefined ? 'u' : options.passive ? 'passive' : 'active';

    return `${target}:${String(eventName)}:${options.capture ? 'capture' : 'bubble'}:${passiveValue}`;
  }
}
